import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import DashboardIcon from "../../assets/icons/sidebar/DashboardIcon";
import HelpCenterIcon from "../../assets/icons/sidebar/HelpCenterIcon";
import CelleraLogo from "../common/CelleraLogo";
import { EPath } from "../../constants/path";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";

type DashboardLayoutSimpleProps = {
  children: React.ReactNode;
  title?: string;
};

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  path?: string;
}

const navigationItems: NavigationItem[] = [
  {
    text: "Dashboard",
    icon: <DashboardIcon />,
    path: EPath.HOME,
  },
];

const DashboardLayoutSimple: React.FC<DashboardLayoutSimpleProps> = ({
  children,
  title = "Admin Dashboard",
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();
  const { logout } = useAuth();
  const { showSuccess, showError } = useToast();

  const getActiveItem = () => {
    const currentPath = location.pathname;
    const activeItem = navigationItems.find(
      (item) => item.path === currentPath,
    );
    return activeItem?.text || "Dashboard";
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNavigationClick = (item: NavigationItem) => {
    if (item.path) {
      navigate(item.path);
    }
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfileClick = () => {
    navigate(EPath.PROFILE);
    handleUserMenuClose();
  };

  const handleLogoutClick = () => {
    setLogoutModalOpen(true);
    handleUserMenuClose();
  };

  const handleLogoutConfirm = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      showSuccess(
        "Logged out successfully",
        "You have been signed out of your account",
      );
      navigate(EPath.LOGIN);
    } catch (error) {
      console.error("Logout failed:", error);
      showError(
        "Logout failed",
        "There was an error signing you out. Please try again.",
      );
    } finally {
      setIsLoggingOut(false);
      setLogoutModalOpen(false);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutModalOpen(false);
  };

  const handleHomeNavigation = () => {
    navigate(EPath.HOME);
  };

  const sidebarContent = (
    <Box
      className={`h-full border-r border-gray-300 transition-all duration-300 flex flex-col ${sidebarCollapsed ? "w-16" : "w-80"}`}
      sx={{
        backgroundColor: "#F6F6F6",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* CELLERA Logo Header Section */}
      <Box
        className="flex items-center justify-between p-4 flex-shrink-0"
        sx={{
          height: 72,
          backgroundColor: "#F6F6F6",
          borderBottom: "1px solid #E8E8E9",
        }}
      >
        {!sidebarCollapsed && (
          <Tooltip title="Go to Home Dashboard" arrow>
            <Box
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity duration-200"
              onClick={handleHomeNavigation}
            >
              <CelleraLogo
                width="120"
                height="36"
                fill="#000080"
                className="mr-3"
              />
            </Box>
          </Tooltip>
        )}
        <IconButton
          onClick={handleSidebarToggle}
          className="text-gray-600"
          size="small"
        >
          {sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
        </IconButton>
      </Box>

      {/* Navigation Menu */}
      <Box className="flex-1 px-4 py-2">
        <List className="space-y-1">
          {navigationItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <Tooltip
                title={sidebarCollapsed ? item.text : ""}
                placement="right"
                arrow
                disableHoverListener={!sidebarCollapsed}
              >
                <ListItemButton
                  onClick={() => handleNavigationClick(item)}
                  className={`mb-1 transition-all duration-200 ${
                    getActiveItem() === item.text ? "" : "hover:bg-gray-100"
                  }`}
                  sx={{
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? "center" : "flex-start",
                    px: sidebarCollapsed ? 1 : 2,
                    backgroundColor:
                      getActiveItem() === item.text && !sidebarCollapsed
                        ? "white"
                        : "transparent",
                    borderRadius: "24px",
                    border:
                      getActiveItem() === item.text && !sidebarCollapsed
                        ? "1px solid #E8E8E9"
                        : "none",
                    "&:hover": {
                      backgroundColor:
                        getActiveItem() === item.text ? "white" : "#f3f4f6",
                      transform: "translateX(2px)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: sidebarCollapsed ? 0 : 2,
                      justifyContent: "center",
                    }}
                  >
                    {React.cloneElement(item.icon, {
                      stroke:
                        getActiveItem() === item.text ? "#000080" : "#6B7280",
                      fill:
                        getActiveItem() === item.text ? "#000080" : "#6B7280",
                    })}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        "& .MuiListItemText-primary": {
                          fontSize: "0.875rem",
                          fontWeight: getActiveItem() === item.text ? 500 : 400,
                          color:
                            getActiveItem() === item.text
                              ? "#000080"
                              : "#374151",
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Help Center - Bottom Section */}
      <Box className="px-4 py-2 border-t border-gray-200 flex-shrink-0">
        <ListItem disablePadding>
          <ListItemButton
            className="rounded-lg hover:bg-gray-100"
            sx={{
              minHeight: 48,
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
              px: sidebarCollapsed ? 1 : 2,
              borderRadius: "24px",
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: sidebarCollapsed ? 0 : 2,
                justifyContent: "center",
              }}
            >
              <HelpCenterIcon stroke="#6B7280" fill="#6B7280" />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText
                primary="Help Center"
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    color: "#374151",
                  },
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box className="min-h-screen flex">
      {/* Sidebar */}
      <Box className="flex-shrink-0">{sidebarContent}</Box>

      {/* Main Content */}
      <Box className="flex-1 flex flex-col">
        {/* Header */}
        <AppBar
          position="static"
          className="bg-white shadow-none"
          elevation={0}
          sx={{
            borderBottom: "1px solid #E8E8E9",
            borderRadius: 0,
          }}
        >
          <Toolbar className="py-4 px-4 h-14 justify-between">
            <Box className="flex items-center gap-3">
              <Typography
                variant="h6"
                className="text-xl font-normal leading-7"
                style={{ color: "#1B1B22" }}
              >
                {title}
              </Typography>
            </Box>
            <Box className="flex items-center gap-4">
              {/* Notifications */}
              <IconButton className="text-gray-600">
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>

              {/* Search */}
              <IconButton className="text-gray-600">
                <SearchIcon />
              </IconButton>

              {/* Settings */}
              <IconButton className="text-gray-600">
                <SettingsIcon />
              </IconButton>

              {/* User Profile */}
              <Box className="flex items-center gap-2">
                <Box className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <Typography className="text-white text-sm font-medium">
                    {user?.displayName?.charAt(0)?.toUpperCase() ||
                      user?.email?.charAt(0)?.toUpperCase() ||
                      "U"}
                  </Typography>
                </Box>
                <Typography className="text-gray-700 text-sm font-medium">
                  {user?.displayName || user?.email?.split("@")[0] || "User"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleUserMenuOpen}
                  className="text-gray-600"
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* User Menu Dropdown */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
          <MenuItem
            onClick={() => {
              navigate(EPath.SETTINGS_RESET_PASSWORD);
              handleUserMenuClose();
            }}
          >
            Reset password
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogoutClick}>Logout</MenuItem>
        </Menu>

        {/* Content Area */}
        <Box className="flex-1 p-4 overflow-auto bg-white">{children}</Box>
      </Box>

      {/* Simple Logout Confirmation Modal */}
      {logoutModalOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              padding: 3,
              borderRadius: 2,
              minWidth: 300,
            }}
          >
            <Typography variant="h6" className="mb-4">
              Confirm Logout
            </Typography>
            <Typography variant="body2" className="mb-4">
              Are you sure you want to logout?
            </Typography>
            <Box className="flex gap-2 justify-end">
              <button
                onClick={handleLogoutCancel}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleLogoutConfirm}
                disabled={isLoggingOut}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {isLoggingOut ? "Logging out..." : "Logout"}
              </button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DashboardLayoutSimple;
